"use client"

import { useCallback, useEffect, useRef, useState } from "react"
import dynamic from "next/dynamic"
import Link from "next/link"
import Image from "next/image"
import { useRouter } from "next/navigation"
import { Briefcase, Maximize2, X as XIcon, ArrowLeft, ArrowRight, Eye, LayoutGrid, Plus, Rows } from "lucide-react"
import { formatCity, formatState } from "@/lib/format-location"
import { HiringFilters } from "@/components/hiring/hiring-filters"
import { JobRolePanel } from "@/components/hiring/job-role-panel"
import { useUserRole } from "@/providers/user-role-provider"
import {
  POSITION_LABELS,
  COMPENSATION_LABELS,
} from "@/lib/hiring-labels"
import { PulseDot } from "@/components/ui/pulse-dot"

const ListingsMap = dynamic(
  () => import("@/components/listings-map").then(m => ({ default: m.ListingsMap })),
  { ssr: false, loading: () => <div className="w-full h-full bg-muted animate-pulse" /> },
)

export interface JobRow {
  id: string
  slug: string | null
  title: string
  position_type: string[]
  compensation: string | null
  compensation_details: string | null
  specialties: string[]
  experience_required: string | null
  clientele_pref?: string[]
  created_at: string
  // Detail fields — fetched with the board query so opening a role inline
  // is a state change, not a request (see JobRolePanel).
  description: string | null
  original_announcement_url?: string | null
  owner_id: string
  shop: {
    id: string
    slug: string | null
    name: string
    logo_url: string | null
    cover_image: string | null
    city: string | null
    state: string | null
    latitude: number | string | null
    longitude: number | string | null
  } | null
}

function timeAgo(iso: string): string {
  const seconds = Math.floor((Date.now() - new Date(iso).getTime()) / 1000)
  if (seconds < 60) return `${seconds}s ago`
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}hr ago`
  const days = Math.floor(hours / 24)
  if (days < 7) return `${days}d ago`
  return `${Math.floor(days / 7)}w ago`
}

// Browse shell ports the silky /listings map UX:
//   • dynamic map height (vh) with rAF + ease-out-quart scroll collapse
//   • pull-to-resize handle between map and listings (Pointer Events +
//     setPointerCapture, snap to {0, 14, default, 70})
//   • Expand-map button + fullscreen overlay
// Listings underneath are compact stacked rows (no cover image).
export function HiringBrowseShell({
  listings,
  previewMode = false,
  createShopPrompt = false,
  isAuthed = false,
  currentUserId = null,
}: {
  listings: JobRow[]
  /** True when an owner is viewing the public board read-only (?preview=1).
   *  Hides the 'View role' / Inquire CTA and renders a return banner. */
  previewMode?: boolean
  /** True for an owner who has no shop yet. They get the board like anyone
   *  else, plus a prompt to create a shop — posting is what needs a shop
   *  (a listing carries a shop_id), browsing is not. */
  createShopPrompt?: boolean
  /** Auth state — controls top padding so logged-out viewers (with the
   *  short public nav) don't get the same big chrome offset as
   *  signed-in viewers (with the tall floating-pill chrome). */
  isAuthed?: boolean
  /** Viewer's user id — lets the inline role panel mirror the detail
   *  page's own-post treatment (Edit shortcut, no self-view counting). */
  currentUserId?: string | null
}) {
  const router = useRouter()
  // Page label flips based on the viewer's mode:
  //   • Owner    → "Hiring"     (they're posting)
  //   • Barber   → "Job Board"  (they're browsing)
  //   • Logged out → "Job Board" (default discovery framing)
  const { role, actualUserType } = useUserRole()
  const effectiveRole = role || actualUserType
  // In preview mode the page title reads as the public board the owner
  // is sneak-peeking at, regardless of their stored role.
  const pageTitle = previewMode ? "Job Board" : (effectiveRole === "owner" ? "Hiring" : "Job Board")

  // ── Map dynamic height + drag/scroll state ─────────────────────
  const [isMobile, setIsMobile] = useState(false)
  // Desktop split flag. The map's height is driven by an INLINE style for the
  // mobile drag-to-resize, and an inline declaration outranks any class — so
  // the lg height utility never applied and the map stayed stuck at ~32svh
  // beside a full-height listings column. Above lg we simply stop writing the
  // inline height and let the class own it.
  const [isLgUp, setIsLgUp] = useState(false)
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)")
    const update = () => setIsLgUp(mq.matches)
    update()
    mq.addEventListener("change", update)
    return () => mq.removeEventListener("change", update)
  }, [])
  const [mapHeight, setMapHeight] = useState(32)
  // Listings view mode — 'list' is the compact single-column row: logo,
  // title, shop and city, the pills, and how long it has been open. 'grid'
  // renders a 2-column tile grid (matches the toggle pattern used on
  // /listings between Rows + LayoutGrid icons).
  //
  // List is the default. The comment here already said so while the code
  // initialised to grid, which is the kind of disagreement that survives
  // because nobody reads both lines at once. On a board the job is to scan
  // a lot of roles quickly, and rows put far more of them on screen at a
  // glance than tiles do.
  const [viewMode, setViewMode] = useState<"list" | "grid">("list")
  const [isDraggingMap, setIsDraggingMap] = useState(false)
  const [fullscreen, setFullscreen] = useState(false)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  // Filter panel open state lifted up so the trigger button can live
  // inline with the open-count row while the chip panel renders below.
  const [filtersOpen, setFiltersOpen] = useState(false)

  // ── Inline role detail ─────────────────────────────────────────
  // "View role" swaps the listings section for the role detail IN PLACE
  // instead of navigating to /hiring/[slug] — the map, filters, and scroll
  // context all stay alive, and closing restores the list exactly where the
  // user left it. The URL still tracks the open role (?role=<slug>) via
  // history.pushState so back/forward and shared links behave like pages,
  // without a server round-trip. /hiring/[slug] remains the SEO/deep-link
  // surface; cmd/middle-click on any role link still opens it.
  const [openJob, setOpenJob] = useState<JobRow | null>(null)
  const detailRef = useRef<HTMLDivElement>(null)
  // One view-count per role per mount — inline opens bypass the detail
  // page's server-side counter, so they report here instead.
  const countedRef = useRef<Set<string>>(new Set())

  const openRole = useCallback((job: JobRow, pushUrl = true) => {
    setOpenJob(job)
    // Every way into a role syncs the map: a list card, a pin, a ?role= URL,
    // a back/forward step. This used to live only in the pin handler, so
    // opening a role from the list left the map showing whichever shop was
    // last tapped — the panel said Berwyn while the map still had Chicago
    // open. The map flies to selectedListingId and fans it out of its
    // cluster, so setting it here is the whole fix.
    setSelectedId(job.id)
    if (pushUrl && typeof window !== "undefined") {
      const url = new URL(window.location.href)
      url.searchParams.set("role", job.slug || job.id)
      window.history.pushState({ boothRole: job.slug || job.id }, "", url)
    }
    const isOwnPost = !!currentUserId && currentUserId === job.owner_id
    if (!previewMode && !isOwnPost && !countedRef.current.has(job.id)) {
      countedRef.current.add(job.id)
      fetch("/api/hiring/track-view", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ listingId: job.id }),
      }).catch(() => {}) // vanity counter — never let it interrupt browsing
    }
    // Bring the panel into view — matters most on mobile where the map
    // occupies the first half of the viewport.
    requestAnimationFrame(() => {
      detailRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })
    })
  }, [previewMode, currentUserId])

  const closeRole = useCallback((pushUrl = true) => {
    setOpenJob(null)
    // Leave no pin selected behind the closed panel, or the next role opened
    // from the list would be the second thing the map had been told about.
    setSelectedId(null)
    if (pushUrl && typeof window !== "undefined") {
      const url = new URL(window.location.href)
      url.searchParams.delete("role")
      window.history.pushState({}, "", url)
    }
  }, [])

  // Deep link (?role=) opens the panel on mount; browser back/forward
  // toggle it like real pages.
  useEffect(() => {
    const syncFromUrl = () => {
      const slug = new URLSearchParams(window.location.search).get("role")
      if (!slug) {
        setOpenJob(null)
        return
      }
      const job = listings.find((j) => (j.slug || j.id) === slug)
      if (job) {
        setOpenJob(job)
      } else {
        // Shared link to a role the current board doesn't hold (filtered
        // out, expired, or beyond the fetch limit) — the full detail page
        // resolves it server-side rather than silently showing the list.
        router.replace(`/hiring/${slug}`)
      }
    }
    syncFromUrl()
    window.addEventListener("popstate", syncFromUrl)
    return () => window.removeEventListener("popstate", syncFromUrl)
  }, [listings, router])

  const dragStateRef = useRef<{ startY: number; startVh: number }>({ startY: 0, startVh: 0 })
  const scrollRafRef = useRef<number>(0)
  const userMaxVhRef = useRef<number>(32)

  // Detect mobile + reset map preference. Default opens to 48svh so the
  // first listing card peeks below the map in the initial viewport
  // (chrome ~12svh + heading ~5svh + map 48svh + meta ~6svh → first
  // card visible from ~71svh down). When the user taps "Expand
  // listings", drop to 14svh (matches /listings).
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 768
      setIsMobile(mobile)
      const preferred = 48
      userMaxVhRef.current = preferred
      setMapHeight(preferred)
    }
    checkMobile()
    window.addEventListener("resize", checkMobile)
    return () => window.removeEventListener("resize", checkMobile)
  }, [])

  // Scroll-driven shrink — desktop only. On mobile the continuous
  // height update was causing the map library to re-paint on every
  // frame against a dark backdrop, which read as a glitchy "stuck"
  // map. Mobile users can collapse the map manually via the
  // splitter handle, which jumps in one snap (no
  // intermediate frames).
  useEffect(() => {
    if (isMobile) return
    const SCROLL_RANGE = 320
    const handleScroll = () => {
      if (isDraggingMap) return
      cancelAnimationFrame(scrollRafRef.current)
      scrollRafRef.current = requestAnimationFrame(() => {
        const scrollTop = window.scrollY
        const progress = Math.min(Math.max(scrollTop / SCROLL_RANGE, 0), 1)
        const eased = 1 - Math.pow(1 - progress, 4)
        const max = userMaxVhRef.current
        // Floor scroll-shrink at 25svh so the badge + zoom controls
        // don't collide. Only floor when the user hasn't explicitly
        // collapsed below it (drag handle).
        const floor = max < 25 ? 0 : 25
        setMapHeight(Math.max(floor, max * (1 - eased)))
      })
    }
    window.addEventListener("scroll", handleScroll, { passive: true })
    return () => {
      window.removeEventListener("scroll", handleScroll)
      cancelAnimationFrame(scrollRafRef.current)
    }
  }, [isMobile, isDraggingMap])

  // Pull-to-resize handlers
  const handleMapDragStart = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault()
    cancelAnimationFrame(scrollRafRef.current)
    ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
    dragStateRef.current = { startY: e.clientY, startVh: mapHeight }
    setIsDraggingMap(true)
  }, [mapHeight])

  const handleMapDragMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!isDraggingMap) return
    const deltaPx = e.clientY - dragStateRef.current.startY
    const deltaVh = (deltaPx / window.innerHeight) * 100
    const next = Math.max(0, Math.min(85, dragStateRef.current.startVh + deltaVh))
    setMapHeight(next)
  }, [isDraggingMap])

  const handleMapDragEnd = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!isDraggingMap) return
    ;(e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId)
    setIsDraggingMap(false)
    const baseDefault = 48
    const anchors = [0, 20, baseDefault, 80]
    const current = mapHeight
    const snapped = anchors.reduce((closest, a) =>
      Math.abs(a - current) < Math.abs(closest - current) ? a : closest
    , anchors[0])
    userMaxVhRef.current = snapped > 0 ? snapped : baseDefault
    setMapHeight(snapped)
  }, [isDraggingMap, isMobile, mapHeight])

  // Cities pulled from the live listings — feeds the city pill row in
  // HiringFilters so users get a real list instead of guessing what's
  // available. Deduped, formatted, sorted; capped at 12 so the pill
  // row doesn't dominate the filter panel on shops in many cities.
  const availableCities = Array.from(
    new Set(
      listings
        .map(j => j.shop?.city)
        .filter((c): c is string => !!c && c.trim().length > 0)
        .map(c => formatCity(c))
    )
  )
    .sort((a, b) => a.localeCompare(b))
    .slice(0, 12)

  // ── Map adapter — translate jobs → ListingsMap shape ───────────
  const mapAdapted = listings
    .filter(j => j.shop && j.shop.latitude != null && j.shop.longitude != null)
    .map(j => {
      const lat = Number(j.shop!.latitude)
      const lng = Number(j.shop!.longitude)
      return {
        id: j.id,
        name: j.shop!.name,
        city: j.shop!.city || "",
        state: j.shop!.state || "",
        monthlyPrice: 0,
        availableBooths: 0,
        booths: 0,
        lat,
        lng,
        slug: j.slug || j.id,
        // These pins are roles, not shops — see Listing.href.
        href: `/hiring/${j.slug || j.id}`,
        cover_image: j.shop!.cover_image || undefined,
        shopType: "Hiring",
        hide_rates: true,
      }
    })
    .filter(l => Number.isFinite(l.lat) && Number.isFinite(l.lng))

  // Tapping a pin is handled entirely inside the map: it opens its preview
  // card and highlights the pin, and the viewer stays where they are. No
  // onListingSelect is passed, deliberately — selectedListingId is only how
  // the map is told to fly to a role opened from somewhere ELSE (a list card,
  // a ?role= link, a back step), so feeding a pin tap back into it would
  // zoom the map out from under the tap.
  //
  // Opening the role used to happen on the pin tap, which blew straight past
  // the card into the full panel: the viewer was moved before they had seen
  // what they'd tapped. It is the card's View that opens it now.
  const handleMapView = (id: string) => {
    const job = listings.find(j => j.id === id)
    if (!job) return
    // The panel lives under the fullscreen map overlay, and openRole scrolls
    // to it — leave fullscreen up and both the scroll and the panel happen
    // somewhere nobody can see.
    setFullscreen(false)
    openRole(job)
  }

  return (
    // Horizontal chrome offset (pill clearance) is supplied by the
    // <PageFrame> wrapper at app/hiring/page.tsx. Vertical chrome
    // offset is conditional on auth: signed-in viewers clear the tall
    // floating-pill chrome (~9rem). Logged-out viewers used to clear
    // PublicNavbar, which was in-flow and reserved its own space, so
    // pt-2 was nearly free. That is now PublicNavGlass — a `fixed`
    // pill reserving no layout space at all — so the heading rendered
    // underneath it. pt-24 clears it, the same way /listings does.
    <div className={`min-h-dvh bg-background pb-28 lg:flex lg:h-dvh lg:flex-col lg:overflow-hidden lg:pb-0 ${
      isAuthed ? "pt-12 md:pt-20" : "pt-24 md:pt-6 lg:pt-8"
    }`}>
      {/* Fullscreen map overlay */}
      {fullscreen && (
        <div className="fixed inset-0 z-50 bg-black" data-floating-nav="hide">
          <ListingsMap listings={mapAdapted} onListingView={handleMapView} isFullscreen entityLabel={{ singular: "listing", plural: "listings" }} />
          {/* Top-right is the only free corner: the map puts zoom at
              bottom-left, "locate me" at bottom-right, and the listing count
              top-left, and bottom-centre belongs to the BOOTH launcher. */}
          <button
            onClick={() => setFullscreen(false)}
            aria-label="Close map"
            className="fixed top-4 right-4 z-[60] inline-flex items-center justify-center gap-2 w-12 h-12 md:w-auto md:h-auto md:px-5 md:py-2.5 bg-[#141414]/90 backdrop-blur-md shadow-lg rounded-full text-sm font-medium border border-white/10 text-white hover:bg-[#141414] transition-colors"
          >
            <XIcon className="w-5 h-5 md:w-4 md:h-4" />
            <span className="hidden md:inline">Close map</span>
          </button>
        </div>
      )}

      {/* Owner-preview banner — black/white pill, thinner text, aligned
          with Job Board heading below + extra mb so it breathes from
          the heading. */}
      {previewMode && (
        <div className="pt-9 md:pt-3 px-2.5 md:px-6 lg:px-0 mb-6">
          <Link
            href="/hiring"
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-light bg-black text-white border border-black/20 hover:bg-zinc-900 transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.5} />
            <Eye className="w-3.5 h-3.5" strokeWidth={1.5} />
            Owner preview · Return to your postings
          </Link>
        </div>
      )}

      {/* Shopless owner — the one thing they can't do here is post, so the
          prompt sits above the board rather than replacing it. */}
      {createShopPrompt && (
        <div className="pt-9 md:pt-3 px-2.5 md:px-6 lg:px-0 mb-6">
          <Link
            href="/dashboard/owner/create-shop"
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-light bg-black text-white border border-black/20 hover:bg-zinc-900 transition-colors"
          >
            <Plus className="w-3.5 h-3.5" strokeWidth={1.5} />
            Create a shop to post your own listing
          </Link>
        </div>
      )}

      {/* Page-header strip — mirror Browse Shops (/listings) gutter + type scale */}
      {/* Logged-out phone: -mt-3 on top of the container's pt-24 puts the
          heading at the same 84px as Browse Shops (/listings), so the two
          boards line up when you move between them. */}
      <div className={`${previewMode ? "pt-2" : isAuthed ? "pt-4" : "-mt-3 pt-0"} md:mt-0 md:pt-5 lg:pt-0 pb-4 px-2.5 md:px-4`}>
        <h1 className="text-2xl md:text-3xl lg:text-[1.4rem] font-bold tracking-tight text-left text-foreground">
          {pageTitle}
        </h1>
      </div>

      {/* Desktop split: listings left, map right.
          Below lg this wrapper is inert (plain block flow), so mobile keeps
          the stacked map-over-listings layout with its drag-to-resize handle
          exactly as before. On lg+ the two become columns — the listings
          column scrolls, the map pins beside it. Order is set per-child so
          the map can stay first in the DOM (mobile reads map-then-listings)
          while rendering on the right. */}
      {/* No lg gutter here — PageFrame already supplies lg:px-8, and
          stacking a second one put ~46px on each side. */}
      {/* On lg the split takes whatever height the header leaves and stops
          15px above the bottom of the window, the same as the side gutter.
          It used to size each column to 100dvh - 9rem, which left ~55px of
          empty page under the map. */}
      <div className="lg:flex lg:min-h-0 lg:flex-1 lg:gap-4 lg:pb-[15px]">

      {/* Map — sticky on mobile/tablet, lg:static on desktop because
          the desktop card uses overflow-hidden for rounded corners. */}
      <div
        className="static w-full px-2.5 md:px-3.5 lg:order-2 lg:flex-1 lg:min-w-0 lg:px-0"
        style={{
          // Inline for both cases on purpose: a Tailwind arbitrary calc()
          // was silently emitting an invalid declaration, so the class did
          // nothing and the map collapsed the moment the inline height was
          // removed on hydration.
          // Matches the listings column beside it — same sticky top-24, same
          // height. The map used to sit 1rem higher, 2.5rem taller, and be
          // pulled up a further -2.75rem, which stacked into its "N listings"
          // badge rendering underneath the floating nav pill. Two columns in
          // a 50/50 split should start on the same line.
          height: isLgUp ? "100%" : `${mapHeight}svh`,
          // No CSS transition during scroll/drag — the rAF-driven setMapHeight
          // already runs at frame rate, so a transition layered on top causes
          // the map's container to snap-back-and-forth and the map library to
          // re-render against a gray bg-muted between frames. Transitions are
          // reserved for drag-snap (handled imperatively in handleMapDragEnd).
          willChange: isLgUp ? undefined : "height",
        }}
      >
        <div className="relative w-full h-full rounded-[2.5rem] overflow-hidden bg-[#141414]">
          <ListingsMap listings={mapAdapted} onListingView={handleMapView} selectedListingId={selectedId} entityLabel={{ singular: "listing", plural: "listings" }} />

          {mapHeight > 10 && (
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-2 z-20">
              <button
                onClick={() => setFullscreen(true)}
                aria-label="Expand map"
                className="inline-flex items-center justify-center gap-1.5 h-7 w-7 md:w-auto md:px-3 md:py-1.5 bg-[#141414]/90 backdrop-blur-md shadow-md rounded-full border border-white/10 hover:bg-[#10b981]/90 hover:border-[#10b981] transition-colors text-xs font-medium text-white"
              >
                <Maximize2 className="w-3 h-3" strokeWidth={2} />
                <span className="hidden md:inline">Expand map</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Drag handle splitter — explicit pull-to-resize */}
      <div
        role="separator"
        aria-orientation="horizontal"
        aria-label="Resize map. Drag up to collapse, down to expand."
        onPointerDown={handleMapDragStart}
        onPointerMove={handleMapDragMove}
        onPointerUp={handleMapDragEnd}
        onPointerCancel={handleMapDragEnd}
        className="relative h-3 -mt-1.5 z-20 flex items-center justify-center cursor-ns-resize touch-none select-none lg:hidden"
      >
        <div
          className={`rounded-full transition-all duration-200 ${
            isDraggingMap
              ? "w-14 h-1 bg-[#10b981] shadow-[0_0_0_4px_rgba(16,185,129,0.15)]"
              : "w-10 h-1 bg-gray-300/80 hover:w-12 hover:h-1.5 hover:bg-gray-400"
          }`}
        />
      </div>

      {/* Below-the-map content — sits on the page bg directly (no
          white card layer). Tightened 2-row layout:
          Row 1 = 'Listings' h2 + open-count (left) | Filters + view toggle (right)
          Row 2 = filter chip panel (only when Filters is open)
          Row 3 = listings, list or grid view.
          On lg+ this whole stack IS the left column of the split, so it
          fills its 40% rather than capping — the map beside it provides the
          right-hand boundary that max-w-4xl used to stand in for. */}
      {/* Left column is a scroll region on the page background, not a card.
          One big white panel wrapping cards made two nested surfaces; the
          listings themselves carry the card treatment now, the same way the
          dashboards and Browse Shops do. */}
      <div
        ref={detailRef}
        className="scroll-mt-24 lg:order-1 lg:h-full lg:w-1/2 lg:shrink-0 lg:min-w-0 lg:overflow-y-auto lg:pr-1"
      >
        {openJob ? (
          // Role detail swapped in place of the listings section — same
          // design as /hiring/[slug], different wrapper. Keyed by id so
          // switching roles re-runs the entrance animation.
          <section className="px-2.5 md:px-4 lg:px-0 pt-4 lg:pt-6 pb-24 lg:pb-4">
            <JobRolePanel
              key={openJob.id}
              job={openJob}
              previewMode={previewMode}
              currentUserId={currentUserId}
              onBack={() => closeRole()}
            />
          </section>
        ) : (
        <>
        <section className="px-2.5 md:px-4 lg:px-0 pt-3 pb-1">
          {/* One-line header: "Listings" with its count, then City and
              Filters, then the view toggle pinned right. It used to be two
              rows (heading + toggle, then City + Filters), which spent a
              whole row of a phone screen on two small pills. Everything is
              h-7 so the line reads as one strip of controls. */}
          <div className="flex items-center gap-2">
            <h2 className="flex shrink-0 items-center gap-1.5 text-base font-semibold tracking-tight text-foreground md:text-lg">
              Listings
              <span
                className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-muted px-1.5 text-[11px] font-medium tabular-nums text-muted-foreground"
                aria-label={`${listings.length} ${listings.length === 1 ? "open role" : "open roles"}`}
              >
                {listings.length}
              </span>
            </h2>
            <div className="flex min-w-0 items-center gap-1.5">
              <HiringFilters
                cities={availableCities}
                slot="city"
                open={filtersOpen}
                onOpenChange={setFiltersOpen}
              />
              <HiringFilters
                cities={availableCities}
                slot="trigger"
                open={filtersOpen}
                onOpenChange={setFiltersOpen}
              />
            </div>
            {/* View toggle — same h-7 as the pills beside it. */}
            <div className="ml-auto flex shrink-0 items-center gap-0.5 rounded-full bg-muted p-0.5">
              <button
                type="button"
                onClick={() => setViewMode("list")}
                aria-label="List view"
                aria-pressed={viewMode === "list"}
                className={`inline-flex h-6 w-7 items-center justify-center rounded-full transition-colors ${
                  viewMode === "list"
                    ? "bg-background shadow-sm text-foreground"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                <Rows className="w-3.5 h-3.5" strokeWidth={1.75} />
              </button>
              <button
                type="button"
                onClick={() => setViewMode("grid")}
                aria-label="Grid view"
                aria-pressed={viewMode === "grid"}
                className={`inline-flex h-6 w-7 items-center justify-center rounded-full transition-colors ${
                  viewMode === "grid"
                    ? "bg-background shadow-sm text-foreground"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                <LayoutGrid className="w-3.5 h-3.5" strokeWidth={1.75} />
              </button>
            </div>
          </div>
        </section>

        {/* Filter chip panel — Row 2 (only when Filters is toggled on).
            Padding matches the heading row above (lg:pl-7) so the first
            chip lines up with 'Listings' and the cards below. */}
        {filtersOpen && (
          <section className="px-2.5 md:px-4 lg:px-0 pt-2 pb-1">
            <HiringFilters
              cities={availableCities}
              slot="panel"
              open={filtersOpen}
              onOpenChange={setFiltersOpen}
            />
          </section>
        )}

        <section className="px-2.5 md:px-4 lg:px-0 pt-3 pb-24 lg:pb-4">
            {listings.length === 0 ? (
              <div className="py-24 text-center text-gray-500">
                <Briefcase className="w-8 h-8 mx-auto mb-3 opacity-40" strokeWidth={1.5} />
                <p className="text-sm">No open positions right now — check back soon.</p>
              </div>
            ) : (
              // Grid is two columns at every width, phone included, so the
              // toggle actually changes the layout; it used to collapse to one
              // column on desktop and look like the rows.
              <div className={viewMode === "grid"
                ? "grid grid-cols-2 gap-2 md:gap-3"
                : "divide-y divide-border border-y border-border"}>
                {listings.map((job) => {
                  const shop = job.shop
                  if (!shop) return null
                  const cityLabel = [formatCity(shop.city || ""), formatState(shop.state || "")].filter(Boolean).join(", ")
                  const shopHref = `/listings/${shop.slug || shop.id}`
                  // Forward preview flag to the detail page so the
                  // Inquire CTA stays suppressed there too.
                  const jobHref = previewMode
                    ? `/hiring/${job.slug || job.id}?preview=1`
                    : `/hiring/${job.slug || job.id}`
                  // Left click opens the role inline; modified clicks
                  // (cmd/ctrl/shift, middle) keep their native new-tab
                  // behavior since the href is the real detail page.
                  const openInline = (e: React.MouseEvent) => {
                    if (e.metaKey || e.ctrlKey || e.shiftKey) return
                    e.preventDefault()
                    openRole(job)
                  }

                  // List view — compact horizontal row per job. No card
                  // chrome; each row reads as a scannable line item
                  // (Linear/Notion-style) so the toggle has real visual
                  // contrast against the gallery-style grid mode above.
                  if (viewMode === "list") {
                    return (
                      // Square, full-width rows. A radius here curved the
                      // divider lines up at both ends of every row.
                      <article key={job.id} className="group flex items-center gap-3 px-1 py-3.5 transition-colors hover:bg-neutral-50 dark:hover:bg-neutral-900">
                        <PulseDot />

                        <Link href={shopHref} className="relative w-9 h-9 rounded-full overflow-hidden bg-muted ring-[0.5px] ring-foreground/10 flex-shrink-0 hover:opacity-80 transition-opacity">
                          {shop.logo_url && (
                            <Image src={shop.logo_url} alt={shop.name} fill className="object-cover" sizes="36px" />
                          )}
                        </Link>

                        {/* min-w floor, not just min-w-0: everything to the
                            right of this (tags, timestamp, arrow) was
                            flex-shrink-0, so in a narrow column the fixed items
                            won and the title collapsed to zero width — a row
                            with three tags lost its heading entirely. */}
                        <div className="min-w-[7rem] flex-1">
                          <Link href={jobHref} onClick={openInline} className="block">
                            <h3 className="text-sm font-semibold truncate group-hover:underline">{job.title}</h3>
                          </Link>
                          <p className="text-xs text-gray-500 truncate mt-0.5">
                            <Link href={shopHref} className="hover:underline">{shop.name}</Link>
                            {cityLabel && <> · {cityLabel}</>}
                          </p>
                        </div>

                        <div className="hidden xl:flex items-center gap-1.5 min-w-0 overflow-hidden">
                          {(job.position_type || []).slice(0, 2).map(p => (
                            <span key={`pt-${p}`} className="text-xs font-normal bg-amber-50 text-amber-700 border border-amber-100 px-2.5 py-0.5 rounded-full">
                              {POSITION_LABELS[p] || p.replace(/_/g, " ")}
                            </span>
                          ))}
                          {job.compensation && (
                            <span className="text-xs font-normal bg-sky-50 text-sky-700 border border-sky-100 px-2.5 py-0.5 rounded-full">
                              {COMPENSATION_LABELS[job.compensation] || job.compensation.replace(/_/g, " ")}
                            </span>
                          )}
                        </div>

                        <span className="hidden sm:inline text-xs text-gray-400 whitespace-nowrap flex-shrink-0">{timeAgo(job.created_at)}</span>

                        <Link
                          href={jobHref}
                          onClick={openInline}
                          aria-label={`View ${job.title}`}
                          className="inline-flex items-center justify-center w-8 h-8 rounded-full text-gray-400 hover:text-foreground hover:bg-muted transition-colors flex-shrink-0"
                        >
                          <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" strokeWidth={2} />
                        </Link>
                      </article>
                    )
                  }

                  // Grid view — a compact card, two to a row. Logo and age
                  // on top, the role as the headline, shop and city under
                  // it, at most two tags, and the way in at the bottom. The
                  // whole card is the link; the shop logo and name keep
                  // their own link to the shop.
                  const tags: { key: string; label: string; cls: string }[] = [
                    ...(job.position_type || []).slice(0, 1).map(p => ({
                      key: `pt-${p}`,
                      label: POSITION_LABELS[p] || p.replace(/_/g, " "),
                      cls: "border-amber-100 bg-amber-50 text-amber-700 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-300",
                    })),
                    ...(job.compensation ? [{
                      key: "comp",
                      label: COMPENSATION_LABELS[job.compensation] || job.compensation.replace(/_/g, " "),
                      cls: "border-sky-100 bg-sky-50 text-sky-700 dark:border-sky-500/20 dark:bg-sky-500/10 dark:text-sky-300",
                    }] : []),
                  ]
                  return (
                    <article
                      key={job.id}
                      className="group relative flex min-w-0 flex-col rounded-2xl bg-card p-3 ring-1 ring-inset ring-foreground/[0.07] transition-[box-shadow,background-color] hover:bg-neutral-50 hover:shadow-[0_6px_20px_rgba(0,0,0,0.06)] dark:bg-neutral-950 dark:hover:bg-neutral-900 md:p-4"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <Link href={shopHref} className="relative z-10 h-9 w-9 shrink-0 overflow-hidden rounded-full bg-muted ring-[0.5px] ring-foreground/10 transition-opacity hover:opacity-80 md:h-10 md:w-10">
                          {shop.logo_url && (
                            <Image src={shop.logo_url} alt={shop.name} fill className="object-cover" sizes="40px" />
                          )}
                        </Link>
                        <span className="inline-flex items-center gap-1.5 pt-0.5 text-[11px] text-muted-foreground whitespace-nowrap">
                          <PulseDot />
                          {timeAgo(job.created_at)}
                        </span>
                      </div>

                      <Link
                        href={jobHref}
                        onClick={openInline}
                        className="mt-3 block after:absolute after:inset-0 after:rounded-2xl after:content-['']"
                      >
                        <h3 className="line-clamp-2 text-[13px] font-semibold leading-snug text-foreground md:text-sm">{job.title}</h3>
                      </Link>
                      <p className="mt-1 truncate text-[11px] text-muted-foreground md:text-xs">
                        <Link href={shopHref} className="relative z-10 hover:underline">{shop.name}</Link>
                        {cityLabel && <> · {cityLabel}</>}
                      </p>

                      {tags.length > 0 && (
                        <div className="mt-3 flex min-w-0 flex-wrap gap-1">
                          {tags.map(t => (
                            <span key={t.key} className={`truncate rounded-full border px-2 py-0.5 text-[10px] font-medium md:text-[11px] ${t.cls}`}>
                              {t.label}
                            </span>
                          ))}
                        </div>
                      )}

                      <span className="mt-auto inline-flex items-center gap-1 self-end pt-3 text-[11px] font-medium text-muted-foreground transition-colors group-hover:text-foreground">
                        {previewMode ? "View" : "View role"}
                        <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" strokeWidth={1.75} />
                      </span>
                    </article>
                  )
                })}
              </div>
            )}
        </section>
        </>
        )}
      </div>

      {/* close desktop split */}
      </div>

    </div>
  )
}
